/**
 * Reset password page
 */

import { useParams, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Lock, ArrowLeft } from 'lucide-react';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { useResetPassword } from '../../hooks/useAuth';
import { strongPasswordSchema } from './password-policy';

const resetPasswordSchema = z.object({
  password: strongPasswordSchema,
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: 'Senhas não conferem',
  path: ['confirmPassword'],
});

type ResetPasswordForm = z.infer<typeof resetPasswordSchema>;

export default function ResetPasswordPage() {
  const { token } = useParams<{ token: string }>();
  const resetPassword = useResetPassword();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordForm>({
    resolver: zodResolver(resetPasswordSchema),
  });

  const onSubmit = (data: ResetPasswordForm) => {
    if (token) {
      resetPassword.mutate({ token, password: data.password });
    }
  };

  if (!token) {
    return (
      <div className="text-center">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Link Inválido</h1>
        <p className="text-gray-600 mb-8">
          O link de recuperação de senha é inválido ou expirou.
        </p>
        <Link to="/recuperar-senha">
          <Button variant="outline" className="w-full">
            Solicitar novo link
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div>
      <div className="text-center mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Nova Senha</h1>
        <p className="text-gray-600 mt-2">Digite sua nova senha</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="relative">
          <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
          <Input
            {...register('password')}
            type="password"
            placeholder="Nova senha"
            className="pl-12"
            error={errors.password?.message}
            autoComplete="new-password"
          />
        </div>

        <div className="relative">
          <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
          <Input
            {...register('confirmPassword')}
            type="password"
            placeholder="Confirme a nova senha"
            className="pl-12"
            error={errors.confirmPassword?.message}
            autoComplete="new-password"
          />
        </div>

        <Button
          type="submit"
          className="w-full"
          loading={resetPassword.isPending}
        >
          Redefinir Senha
        </Button>
      </form>

      <p className="mt-8 text-center">
        <Link to="/entrar" className="text-primary-600 hover:text-primary-700 inline-flex items-center gap-2">
          <ArrowLeft className="w-4 h-4" />
          Voltar para o login
        </Link>
      </p>
    </div>
  );
}
